/**
 * Analytics Context Provider
 *
 * Provides PostHog tracking capabilities throughout the application.
 * Uses the official PostHog React hooks and automatically handles user identification.
 */
import React, {
  createContext,
  useContext,
  useEffect,
  useCallback,
  useMemo,
  ReactNode,
} from 'react'
import { usePostHog } from 'posthog-js/react'
import { useAuthStore } from '../stores/authStore'
import {
  AnalyticsEvents,
  AnalyticsEventName,
  AnalyticsEventProperties,
  PostHogUserProperties,
} from '../types/analytics'

// Environment configuration
const ENV = import.meta.env.VITE_ENV
const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY

// ============================================
// CONTEXT TYPES
// ============================================

interface AnalyticsContextValue {
  /** Track a custom event */
  track: (
    eventName: AnalyticsEventName,
    properties?: AnalyticsEventProperties
  ) => void

  /** Track a manual page view (automatic is enabled by default) */
  trackPageView: (pageName: string, properties?: Record<string, unknown>) => void

  /** Check if a boolean feature flag is enabled */
  isFeatureEnabled: (flagKey: string) => boolean

  /** Get a feature flag value (boolean or string) */
  getFeatureFlag: (flagKey: string) => boolean | string | undefined

  /** Reload feature flags from PostHog */
  reloadFeatureFlags: () => void

  /** Opt user out of tracking */
  optOut: () => void

  /** Opt user back into tracking */
  optIn: () => void

  /** Check if user has opted out */
  hasOptedOut: () => boolean

  /** Event name constants for easy access */
  events: typeof AnalyticsEvents

  /** Whether analytics is currently enabled */
  isEnabled: boolean
}

const AnalyticsContext = createContext<AnalyticsContextValue | undefined>(
  undefined
)

// ============================================
// HELPER FUNCTION
// ============================================

/**
 * Check if PostHog should be enabled.
 * Only enabled in production environment with a valid API key.
 */
const isAnalyticsEnabled = (): boolean => {
  return ENV === 'production' && !!POSTHOG_KEY
}

// ============================================
// PROVIDER COMPONENT
// ============================================

interface AnalyticsProviderProps {
  children: ReactNode
}

export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({
  children,
}) => {
  const posthog = usePostHog()
  const { user, isAuthenticated } = useAuthStore()

  // Identify user when authenticated
  useEffect(() => {
    if (!isAnalyticsEnabled() || !posthog) return

    if (isAuthenticated && user) {
      const userProperties: PostHogUserProperties = {
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        organizationId: user.organizationId,
        organizationName: user.organizationName,
        roleName: user.roleName,
        subscriptionTier: user.subscriptionTier,
        subscriptionStatus: user.subscriptionStatus || undefined,
        customerId: user.customerId,
        onboardingStatus: user.onboardingStatus,
      }

      // Use customerId as the primary identifier, fallback to email
      const userId = user.customerId || user.email

      posthog.identify(userId, {
        email: userProperties.email,
        name: `${userProperties.firstName} ${userProperties.lastName}`,
        first_name: userProperties.firstName,
        last_name: userProperties.lastName,
        organization_id: userProperties.organizationId,
        organization_name: userProperties.organizationName,
        role: userProperties.roleName,
        subscription_tier: userProperties.subscriptionTier,
        subscription_status: userProperties.subscriptionStatus,
        customer_id: userProperties.customerId,
        onboarding_status: userProperties.onboardingStatus,
      })

      // Register super properties (included with every event)
      posthog.register({
        organization_id: userProperties.organizationId,
        subscription_tier: userProperties.subscriptionTier,
        user_role: userProperties.roleName,
      })
    }
  }, [isAuthenticated, user, posthog])

  // Reset identity on logout
  useEffect(() => {
    if (!isAnalyticsEnabled() || !posthog) return

    if (!isAuthenticated) {
      posthog.reset()
    }
  }, [isAuthenticated, posthog])

  // Track events
  const track = useCallback(
    (eventName: AnalyticsEventName, properties?: AnalyticsEventProperties) => {
      if (!isAnalyticsEnabled() || !posthog) return

      posthog.capture(eventName, {
        ...properties,
        timestamp: new Date().toISOString(),
      })
    },
    [posthog]
  )

  // Track page views (for manual tracking if needed)
  const trackPageView = useCallback(
    (pageName: string, properties?: Record<string, unknown>) => {
      if (!isAnalyticsEnabled() || !posthog) return

      posthog.capture('$pageview', {
        page_name: pageName,
        ...properties,
      })
    },
    [posthog]
  )

  // Feature flag methods
  const isFeatureEnabled = useCallback(
    (flagKey: string) => {
      if (!isAnalyticsEnabled() || !posthog) return false
      return posthog.isFeatureEnabled(flagKey) ?? false
    },
    [posthog]
  )

  const getFeatureFlag = useCallback(
    (flagKey: string) => {
      if (!isAnalyticsEnabled() || !posthog) return undefined
      return posthog.getFeatureFlag(flagKey)
    },
    [posthog]
  )

  const reloadFeatureFlags = useCallback(() => {
    if (!isAnalyticsEnabled() || !posthog) return
    posthog.reloadFeatureFlags()
  }, [posthog])

  // Privacy controls
  const optOut = useCallback(() => {
    if (!isAnalyticsEnabled() || !posthog) return
    posthog.opt_out_capturing()
  }, [posthog])

  const optIn = useCallback(() => {
    if (!isAnalyticsEnabled() || !posthog) return
    posthog.opt_in_capturing()
  }, [posthog])

  const hasOptedOut = useCallback(() => {
    if (!isAnalyticsEnabled() || !posthog) return true
    return posthog.has_opted_out_capturing()
  }, [posthog])

  // Memoized context value
  const contextValue: AnalyticsContextValue = useMemo(
    () => ({
      track,
      trackPageView,
      isFeatureEnabled,
      getFeatureFlag,
      reloadFeatureFlags,
      optOut,
      optIn,
      hasOptedOut,
      events: AnalyticsEvents,
      isEnabled: isAnalyticsEnabled(),
    }),
    [
      track,
      trackPageView,
      isFeatureEnabled,
      getFeatureFlag,
      reloadFeatureFlags,
      optOut,
      optIn,
      hasOptedOut,
    ]
  )

  return (
    <AnalyticsContext.Provider value={contextValue}>
      {children}
    </AnalyticsContext.Provider>
  )
}

// ============================================
// MAIN HOOK
// ============================================

/**
 * Hook to access analytics functionality.
 * Must be used within an AnalyticsProvider.
 */
export const useAnalytics = (): AnalyticsContextValue => {
  const context = useContext(AnalyticsContext)
  if (!context) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider')
  }
  return context
}

// ============================================
// CONVENIENCE HOOKS
// ============================================

/**
 * Hook for tracking authentication events.
 */
export const useAuthTracking = () => {
  const { track, events } = useAnalytics()

  return {
    trackLoginSuccess: (rememberMe?: boolean) =>
      track(events.AUTH_LOGIN_SUCCESS, { rememberMe }),

    trackLoginFailed: (errorMessage?: string) =>
      track(events.AUTH_LOGIN_FAILED, { errorMessage }),

    trackLogout: () => track(events.AUTH_LOGOUT),

    trackRegistrationStarted: (tierId?: number, billingCycle?: string) =>
      track(events.AUTH_REGISTRATION_STARTED, { tierId, billingCycle }),

    trackRegistrationCompleted: () => track(events.AUTH_REGISTRATION_COMPLETED),

    trackRegistrationFailed: (errorMessage?: string) =>
      track(events.AUTH_REGISTRATION_FAILED, { errorMessage }),

    trackPasswordResetRequested: () =>
      track(events.AUTH_PASSWORD_RESET_REQUESTED),

    trackPasswordResetCompleted: () =>
      track(events.AUTH_PASSWORD_RESET_COMPLETED),

    trackEmailVerified: () => track(events.AUTH_EMAIL_VERIFIED),

    trackInvitationAccepted: () => track(events.AUTH_INVITATION_ACCEPTED),
  }
}

/**
 * Hook for tracking subscription/billing events.
 */
export const useSubscriptionTracking = () => {
  const { track, events } = useAnalytics()

  return {
    trackPlanViewed: (tierId: number, tierName: string) =>
      track(events.SUBSCRIPTION_PLAN_VIEWED, { tierId, tierName }),

    trackPlanSelected: (
      tierId: number,
      tierName: string,
      billingCycle: 'monthly' | 'annual'
    ) =>
      track(events.SUBSCRIPTION_PLAN_SELECTED, {
        tierId,
        tierName,
        billingCycle,
      }),

    trackCheckoutStarted: (
      tierId: number,
      billingCycle: 'monthly' | 'annual'
    ) => track(events.SUBSCRIPTION_CHECKOUT_STARTED, { tierId, billingCycle }),

    trackCheckoutCompleted: (
      tierId: number,
      amount: number,
      currency: string
    ) =>
      track(events.SUBSCRIPTION_CHECKOUT_COMPLETED, {
        tierId,
        amount,
        currency,
      }),

    trackCheckoutCanceled: (tierId?: number) =>
      track(events.SUBSCRIPTION_CHECKOUT_CANCELED, { tierId }),

    trackUpgraded: (previousTier: string, newTier: string, amount: number) =>
      track(events.SUBSCRIPTION_UPGRADED, { previousTier, newTier, amount }),

    trackDowngraded: (previousTier: string, newTier: string) =>
      track(events.SUBSCRIPTION_DOWNGRADED, { previousTier, newTier }),

    trackCanceled: (reason?: string) =>
      track(events.SUBSCRIPTION_CANCELED, { cancellationReason: reason }),

    trackPaymentMethodAdded: () =>
      track(events.SUBSCRIPTION_PAYMENT_METHOD_ADDED),

    trackPaymentMethodRemoved: () =>
      track(events.SUBSCRIPTION_PAYMENT_METHOD_REMOVED),
  }
}

/**
 * Hook for tracking feature usage events.
 */
export const useFeatureTracking = () => {
  const { track, events } = useAnalytics()

  return {
    trackDashboardViewed: (view: string, customerId?: string) =>
      track(events.DASHBOARD_VIEWED, { view, customerId }),

    trackSocialMediaAnalyticsViewed: (customerId?: string, period?: string) =>
      track(events.SOCIAL_MEDIA_ANALYTICS_VIEWED, { customerId, period }),

    trackReportGenerated: (reportType: string, customerId?: string) =>
      track(events.REPORT_GENERATED, { reportType, customerId }),

    trackReportDownloaded: (reportType: string, format: string) =>
      track(events.REPORT_DOWNLOADED, { reportType, format }),

    trackFilterApplied: (filterType: string, filterValue: string | string[]) =>
      track(events.FILTER_APPLIED, { filterType, filterValue }),

    trackDateRangeChanged: (
      period: string,
      customRange?: { start: string; end: string }
    ) => track(events.DATE_RANGE_CHANGED, { period, customRange }),

    trackPlatformSelected: (platformIds: string[]) =>
      track(events.PLATFORM_SELECTED, { platformIds }),

    trackPostInsightsViewed: (postId: string) =>
      track(events.POST_INSIGHTS_VIEWED, { postId }),

    trackBrandDetailsViewed: (brandId: string, brandName: string) =>
      track(events.BRAND_DETAILS_VIEWED, { brandId, brandName }),

    trackCompetitiveAnalysisViewed: () =>
      track(events.COMPETITIVE_ANALYSIS_VIEWED),

    trackAIIntelligenceUsed: (featureType: string) =>
      track(events.AI_INTELLIGENCE_USED, { featureType }),
  }
}

/**
 * Hook for tracking navigation events.
 */
export const useNavigationTracking = () => {
  const { track, events } = useAnalytics()

  return {
    trackSidebarNavigation: (from: string, to: string) =>
      track(events.SIDEBAR_NAVIGATION, { from, to }),

    trackDrawerOpened: (drawerType: string, data?: Record<string, unknown>) =>
      track(events.DRAWER_OPENED, { drawerType, ...data }),

    trackDrawerClosed: (drawerType: string) =>
      track(events.DRAWER_CLOSED, { drawerType }),

    trackTabChanged: (tabId: string, from?: string) =>
      track(events.TAB_CHANGED, { tabId, from }),

    trackSettingsTabViewed: (tabId: string) =>
      track(events.SETTINGS_TAB_VIEWED, { tabId }),
  }
}

/**
 * Hook for tracking report events.
 */
export const useReportTracking = () => {
  const { track, events } = useAnalytics()

  return {
    trackReportGenerated: (
      reportType: string,
      format: 'pdf' | 'csv',
      customerId?: string
    ) => track(events.REPORT_GENERATED, { reportType, format, customerId }),

    trackReportGenerationFailed: (
      reportType: string,
      errorMessage: string,
      customerId?: string
    ) =>
      track(events.REPORT_GENERATION_FAILED, {
        reportType,
        errorMessage,
        customerId,
      }),

    trackScheduleCreated: (
      reportType: string,
      scheduleType: 'one-time' | 'recurring',
      frequency: string | undefined,
      emailEnabled: boolean,
      customerId?: string
    ) =>
      track(events.REPORT_SCHEDULE_CREATED, {
        reportType,
        scheduleType,
        frequency,
        emailEnabled,
        customerId,
      }),

    trackScheduleUpdated: (
      scheduleId: string,
      reportType: string,
      customerId?: string
    ) =>
      track(events.REPORT_SCHEDULE_UPDATED, {
        scheduleId,
        reportType,
        customerId,
      }),

    trackScheduleDeleted: (
      scheduleId: string,
      reportType: string,
      customerId?: string
    ) =>
      track(events.REPORT_SCHEDULE_DELETED, {
        scheduleId,
        reportType,
        customerId,
      }),

    trackScheduleStatusToggled: (
      scheduleId: string,
      newStatus: 'active' | 'paused',
      customerId?: string
    ) =>
      track(events.REPORT_SCHEDULE_STATUS_TOGGLED, {
        scheduleId,
        newStatus,
        customerId,
      }),

    trackScheduleRunNow: (
      scheduleId: string,
      reportType: string,
      customerId?: string
    ) =>
      track(events.REPORT_SCHEDULE_RUN_NOW, {
        scheduleId,
        reportType,
        customerId,
      }),
  }
}
